import { useMutation, UseMutationOptions, UseMutationResult, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import { IReportCreateResponse, IReportsListResponse, IReportUploadPayload, IJobsPeriodRow, IRevenuePeriodRow, ITechUtilisationRow } from "../../../types/api/reports";
import { IVehicle } from "../../../types/api/vehicle";
import { IDbsCheck, IRtwCheck } from "../compliance/callbacks";
import { downloadReport, reportsList, saveReport, createReport, fetchJobsPerPeriod, fetchRevenuePerPeriod, fetchTechUtilisation, fetchFleetReport, fetchDbsReport, fetchRtwReport } from "./callbacks";



export const useReportsList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IReportsListResponse[]>, Error>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<TResponseWithQData<IReportsListResponse[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IReportsListResponse[]>, Error>({
    queryKey: [API_ENDPOINTS.REPORTS_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await reportsList(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map(x => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total }
    }),
    initialData: {
      data: [],
      success: true,
      message: '',
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useReportSave = (
  { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IReportsListResponse>) => void;
    onError?: (error: unknown) => void;
  }
  ,
  options: UseMutationOptions<TResponse<IReportsListResponse>, unknown, IReportUploadPayload> = {}
): UseMutationResult<TResponse<IReportsListResponse>, unknown, IReportUploadPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IReportsListResponse>, unknown, IReportUploadPayload>({
    mutationFn: async (data: IReportUploadPayload) => {
      const response = await saveReport(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.setQueryData([API_ENDPOINTS.REPORTS_LIST, { ...qData, total: null }], (old: any) => {
        if (!old) return;
        const exists = old.data.find((v: any) => v._id === res.data._id);
        return {
          ...old,
          data: exists
            ? old.data.map((v: any) => (v._id === res.data._id ? res.data : v))
            : [res.data, ...old.data]
        };
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options
  });
};

export const useCreateReport = (
  { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IReportCreateResponse>) => void;
    onError?: (error: unknown) => void;
  }
  ,
  options: UseMutationOptions<TResponse<IReportCreateResponse>, unknown, IReportCreateResponse> = {}
): UseMutationResult<TResponse<IReportCreateResponse>, unknown, IReportCreateResponse> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IReportCreateResponse>, unknown, IReportCreateResponse>({
    mutationFn: async (data: IReportCreateResponse) => {
      const response = await createReport(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.setQueryData([API_ENDPOINTS.REPORTS_LIST, { ...qData, total: null }], (old: any) => {
        if (!old) return;
        const exists = old.data.find((v: any) => v._id === res.data._id);
        return {
          ...old,
          data: exists
            ? old.data.map((v: any) => (v._id === res.data._id ? res.data : v))
            : [res.data, ...old.data]
        };
      });

        queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.REPORTS_LIST]
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options
  });
};


// export const useDownloadReport = (
//   id: string,
//   options?: Omit<
//     UseQueryOptions<Blob, Error>,
//     "queryKey" | "queryFn"
//   >
// ): UseQueryResult<Blob, Error> => {
//   return useQuery<Blob, Error>({
//     queryKey: [API_ENDPOINTS.REPORTS_DOWNLOAD, id],
//     queryFn: () => downloadReport(id),
//     enabled: !!id,
//     refetchOnWindowFocus: false,
//     staleTime: 0, // important for downloads
//     ...options,
//   });
// };

export const useJobsPerPeriod = (from: string, to: string, group_by = "month") =>
  useQuery<TResponse<IJobsPeriodRow[]>, Error>({
    queryKey: [API_ENDPOINTS.REPORTS_JOBS_PER_PERIOD, from, to, group_by],
    queryFn: async () => (await fetchJobsPerPeriod(from, to, group_by)).data,
    enabled: !!from && !!to,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

export const useRevenuePerPeriod = (from: string, to: string, group_by = "month") =>
  useQuery<TResponse<IRevenuePeriodRow[]>, Error>({
    queryKey: [API_ENDPOINTS.REPORTS_REVENUE_PER_PERIOD, from, to, group_by],
    queryFn: async () => (await fetchRevenuePerPeriod(from, to, group_by)).data,
    enabled: !!from && !!to,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

export const useTechUtilisation = (from: string, to: string) =>
  useQuery<TResponse<ITechUtilisationRow[]>, Error>({
    queryKey: [API_ENDPOINTS.REPORTS_TECH_UTILISATION, from, to],
    queryFn: async () => (await fetchTechUtilisation(from, to)).data,
    enabled: !!from && !!to,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

export const useFleetReport = () =>
  useQuery<TResponseWithQData<IVehicle[]>, Error>({
    queryKey: ['fleet-report'],
    queryFn: async () => (await fetchFleetReport()).data,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

export const useDbsReport = () =>
  useQuery<TResponseWithQData<IDbsCheck[]>, Error>({
    queryKey: ['dbs-report'],
    queryFn: async () => (await fetchDbsReport()).data,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

export const useRtwReport = () =>
  useQuery<TResponseWithQData<IRtwCheck[]>, Error>({
    queryKey: ['rtw-report'],
    queryFn: async () => (await fetchRtwReport()).data,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

